import { useState } from "react";
import { BookOpen, Check, FileDown, FileText, History, Loader2, Pencil, Search, Trash2, X } from "lucide-react";

const PAPER_TYPE_LABELS = {
  literature_review: "Literature review",
  journal_article: "Journal article",
  thesis_chapter: "Thesis chapter",
  technical_report: "Technical report",
};

const formatDraftDate = (iso) => {
  try {
    return new Date(iso).toLocaleDateString(undefined, { year: "numeric", month: "short", day: "numeric" });
  } catch {
    return "";
  }
};

export const DraftHistoryPanel = ({
  history, activeProjectId, openingDraftId, pdfLoadingId, docxLoadingId,
  onOpen, onPdf, onDocx, onRename, onDelete,
}) => {
  const [renamingId, setRenamingId] = useState(null);
  const [renameValue, setRenameValue] = useState("");
  const [savingRename, setSavingRename] = useState(false);
  const [deletingId, setDeletingId] = useState(null);
  const [query, setQuery] = useState("");

  if (!history.length) return null;

  const q = query.trim().toLowerCase();
  const filtered = q
    ? history.filter((d) => `${d.title || ""} ${d.prompt || ""}`.toLowerCase().includes(q))
    : history;

  const startRename = (draft) => {
    setRenamingId(draft.id);
    setRenameValue(draft.title || draft.prompt.slice(0, 120));
  };

  const saveRename = async () => {
    const title = renameValue.trim();
    if (!title || savingRename) return;
    setSavingRename(true);
    const ok = await onRename(renamingId, title);
    setSavingRename(false);
    if (ok) setRenamingId(null);
  };

  const handleDelete = async (draftId) => {
    if (deletingId) return;
    setDeletingId(draftId);
    await onDelete(draftId);
    setDeletingId(null);
  };

  return (
    <section className="bg-white border border-slate-200 rounded-xl shadow-sm p-5" data-testid="research-history-panel">
      <div className="flex items-center gap-2 mb-4">
        <History className="w-4 h-4 text-orange-600" />
        <h2 className="text-xs font-semibold uppercase tracking-[0.16em] text-slate-600">
          Past drafts ({q ? `${filtered.length}/${history.length}` : history.length})
        </h2>
      </div>
      {history.length > 3 && (
        <div className="relative mb-3">
          <Search className="w-3.5 h-3.5 text-slate-400 absolute left-2.5 top-1/2 -translate-y-1/2" />
          <input
            type="text"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="Search drafts by title or topic…"
            className="w-full h-9 rounded-md border border-slate-200 bg-slate-50 pl-8 pr-8 text-xs text-slate-800 placeholder:text-slate-400 focus:outline-none focus:ring-2 focus:ring-orange-400 focus:bg-white"
            data-testid="research-history-search"
          />
          {query && (
            <button
              type="button"
              onClick={() => setQuery("")}
              className="absolute right-2 top-1/2 -translate-y-1/2 p-0.5 rounded text-slate-400 hover:text-slate-700"
              title="Clear search"
              data-testid="research-history-search-clear"
            >
              <X className="w-3.5 h-3.5" />
            </button>
          )}
        </div>
      )}
      {q && filtered.length === 0 && (
        <p className="text-xs text-slate-400 py-3 text-center" data-testid="research-history-no-results">
          No drafts match “{query.trim()}”.
        </p>
      )}
      <div className="space-y-2.5" data-testid="research-history-list">
        {filtered.map((draft) => (
          <div
            key={draft.id}
            className={`rounded-lg border p-3 transition-colors ${activeProjectId === draft.id ? "border-orange-400 bg-orange-50/60" : "border-slate-200 hover:border-orange-300"}`}
            data-testid={`research-history-item-${draft.id}`}
          >
            {renamingId === draft.id ? (
              <div className="flex items-center gap-1.5">
                <input
                  type="text"
                  value={renameValue}
                  onChange={(e) => setRenameValue(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === "Enter") saveRename();
                    if (e.key === "Escape") setRenamingId(null);
                  }}
                  maxLength={200}
                  autoFocus
                  className="flex-1 min-w-0 h-8 rounded border border-orange-300 px-2 text-sm text-slate-800 focus:outline-none focus:ring-2 focus:ring-orange-400"
                  data-testid="research-rename-input"
                />
                <button
                  type="button"
                  onClick={saveRename}
                  disabled={savingRename || !renameValue.trim()}
                  className="shrink-0 p-1.5 rounded text-emerald-700 hover:bg-emerald-50 disabled:opacity-40"
                  title="Save"
                  data-testid="research-rename-save"
                >
                  {savingRename ? <Loader2 className="w-4 h-4 animate-spin" /> : <Check className="w-4 h-4" />}
                </button>
                <button
                  type="button"
                  onClick={() => setRenamingId(null)}
                  className="shrink-0 p-1.5 rounded text-slate-500 hover:bg-slate-100"
                  title="Cancel"
                  data-testid="research-rename-cancel"
                >
                  <X className="w-4 h-4" />
                </button>
              </div>
            ) : (
              <button type="button" onClick={() => onOpen(draft.id)} className="block w-full text-left" data-testid={`research-history-open-${draft.id}`}>
                <p className="text-sm text-slate-800 leading-5 line-clamp-2">{draft.title || draft.prompt}</p>
                <p className="text-[11px] text-slate-400 mt-1.5">
                  {formatDraftDate(draft.created_at)} · {PAPER_TYPE_LABELS[draft.paper_type] || draft.paper_type} · {draft.citation_style}
                </p>
              </button>
            )}
            <div className="flex items-center gap-4 mt-2">
              <button type="button" onClick={() => onOpen(draft.id)} className="inline-flex items-center gap-1 text-[11px] font-semibold text-slate-600 hover:text-slate-900">
                {openingDraftId === draft.id ? <Loader2 className="w-3 h-3 animate-spin" /> : <BookOpen className="w-3 h-3" />} Open
              </button>
              <button
                type="button"
                onClick={() => onPdf(draft.id)}
                className="inline-flex items-center gap-1 text-[11px] font-semibold text-orange-700 hover:text-orange-900"
                data-testid={`research-history-pdf-${draft.id}`}
              >
                {pdfLoadingId === draft.id ? <Loader2 className="w-3 h-3 animate-spin" /> : <FileDown className="w-3 h-3" />} PDF
              </button>
              <button
                type="button"
                onClick={() => onDocx(draft.id)}
                className="inline-flex items-center gap-1 text-[11px] font-semibold text-sky-700 hover:text-sky-900"
                data-testid={`research-history-docx-${draft.id}`}
              >
                {docxLoadingId === draft.id ? <Loader2 className="w-3 h-3 animate-spin" /> : <FileText className="w-3 h-3" />} Word
              </button>
              <span className="flex-1" />
              <button
                type="button"
                onClick={() => startRename(draft)}
                className="inline-flex items-center gap-1 text-[11px] font-semibold text-slate-500 hover:text-slate-800"
                title="Rename draft"
                data-testid={`research-history-rename-${draft.id}`}
              >
                <Pencil className="w-3 h-3" /> Rename
              </button>
              <button
                type="button"
                onClick={() => handleDelete(draft.id)}
                className="inline-flex items-center gap-1 text-[11px] font-semibold text-red-600 hover:text-red-800"
                title="Delete draft"
                data-testid={`research-history-delete-${draft.id}`}
              >
                {deletingId === draft.id ? <Loader2 className="w-3 h-3 animate-spin" /> : <Trash2 className="w-3 h-3" />} Delete
              </button>
            </div>
          </div>
        ))}
      </div>
    </section>
  );
};
